import React, { useEffect, useState } from 'react';
import styles from './ComparisonResult.module.scss';
import {
  ComparisonReportResponse,
  IComparisonResult
} from './ComparisonResult.interface';
import { getBusinessArea, useAppSelector } from 'app/redux';
import { LoadingSpinner } from 'app/components';
import { getComparisonReport, getComparisonStatusStream } from 'integration';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as DiagramIcon } from 'app/assets/Icons/Chips/Diagram.svg';
import { ReactComponent as PackageIcon } from 'app/assets/Icons/Chips/Package.svg';
import { ReactComponent as TargetIcon } from 'app/assets/Icons/Chips/Target.svg';
import { displayToastError } from 'app/utils';
import classnames from 'classnames';

const processHtmlContent = (html: string): string => {
  const script = `<script>
document.addEventListener('click', function(e) {
  var link = e.target.closest('a[href^="#"]');
  if (link) {
    e.preventDefault();
    var target = document.querySelector(link.getAttribute('href'));
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  }
});
</script>`;
  return html.includes('</body>')
    ? html.replace('</body>', script + '</body>')
    : html + script;
};

export const ComparisonResult: React.FC<IComparisonResult> = ({
  comparisonId,
  isProcessing = false,
  streamReader,
  onComparisonFailure
}) => {
  const currentService = 'document-compare';
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';

  type TabType = 'combined' | 'outline' | 'sideBySide';
  const TAB_STORAGE_KEY = 'docCompare.defaultTab';
  const storedTab = localStorage.getItem(TAB_STORAGE_KEY) as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(storedTab ?? 'combined');

  const handleTabChange = (tab: TabType): void => {
    setActiveTab(tab);
    localStorage.setItem(TAB_STORAGE_KEY, tab);
  };
  const [comparisonReport, setComparisonReport] =
    useState<ComparisonReportResponse | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [progress, setProgress] = useState<number | null>(null);
  const [displayProgress, setDisplayProgress] = useState<number>(0);

  useEffect(() => {
    if (progress === null) return;
    const target = progress;
    const interval = setInterval(() => {
      setDisplayProgress((prev) => {
        if (prev >= target) {
          clearInterval(interval);
          return prev;
        }
        return Math.min(prev + 1, target);
      });
    }, 30);
    return () => clearInterval(interval);
  }, [progress]);

  const getProgressLabel = (pct: number): string => {
    if (pct < 5) return 'Setting up comparison...';
    if (pct < 10) return 'Reading your documents...';
    if (pct < 75) return 'Comparing documents...';
    if (pct < 80) return 'Generating AI summary...';
    return 'Preparing your report...';
  };

  useEffect(() => {
    const initializeComparison = (): void => {
      if (isProcessing) {
        handleProcessingComparison();
      } else {
        fetchComparisonReport();
      }
    };

    initializeComparison();
  }, []);

  const handleProcessingComparison = (): void => {
    setIsLoading(true);
    setProgress(0);

    const processStream = async (): Promise<void> => {
      try {
        const reader =
          streamReader ??
          (await getComparisonStatusStream(
            currentBusinessArea,
            currentService,
            comparisonId
          ));

        const decoder = new TextDecoder();
        let done = false;

        while (!done) {
          const result = await reader.read();
          done = result.done;
          if (result.done) break;

          const chunk = decoder.decode(result.value, { stream: true });
          const events = chunk.split('\n\n').filter(Boolean);

          for (let i = 0; i < events.length; i++) {
            const event = events[i];
            const dataLine = event
              .split('\n')
              .find((line) => line.startsWith('data:'));
            if (!dataLine) continue;

            let parsed: { status: string };
            try {
              parsed = JSON.parse(dataLine.replace(/^data:\s*/, ''));
            } catch {
              continue;
            }

            const match = parsed.status.match(/(\d+)%/);
            if (match) {
              setProgress(parseInt(match[1], 10));
            }

            if (parsed.status === '100%') {
              reader.cancel();
              fetchComparisonReport();
              return;
            } else if (parsed.status === '0%') {
              setProgress(null);
              setIsLoading(false);
              reader.cancel();
              displayToastError('Comparison failed, please try again.');
              onComparisonFailure();
              return;
            }
          }
        }
      } catch (error) {
        console.error('SSE Error:', error);
        setIsLoading(false);
      }
    };

    processStream();
  };

  const fetchComparisonReport = (): void => {
    setIsLoading(true);
    setProgress(100);
    getComparisonReport(currentBusinessArea, currentService, comparisonId)
      .then((response) => {
        setComparisonReport(response);
      })
      .catch((error) => {
        console.error('Error:', error);
      })
      .finally(() => {
        setProgress(null);
        setIsLoading(false);
      });
  };

  const renderTabContent = (): JSX.Element | null => {
    // resultFile1 = combined, resultFile2 = withcallouts (outline), resultFile3 = sidebyside
    switch (activeTab) {
      case 'combined':
        return comparisonReport!.resultFile1 ? (
          <iframe
            key="combined"
            title="Combined View"
            srcDoc={processHtmlContent(comparisonReport!.resultFile1)}
            className={styles.iframe}
            sandbox="allow-scripts allow-same-origin"
          />
        ) : null;
      case 'outline':
        return comparisonReport!.resultFile2 ? (
          <iframe
            key="outline"
            title="Outline View"
            srcDoc={processHtmlContent(comparisonReport!.resultFile2)}
            className={styles.iframe}
            sandbox="allow-scripts allow-same-origin"
          />
        ) : null;
      case 'sideBySide':
        return comparisonReport!.resultFile3 ? (
          <iframe
            key="sideBySide"
            title="Side by Side View"
            srcDoc={processHtmlContent(comparisonReport!.resultFile3)}
            className={styles.iframe}
            sandbox="allow-scripts allow-same-origin"
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className={styles['comparison-result']}>
      <h2 className={styles['report-name']}>{comparisonReport?.name}</h2>

      <div className={styles.tabs}>
        <Button
          aria-label="Combined View"
          variant="tertiary"
          size="small"
          onClick={() => handleTabChange('combined')}
          className={classnames({
            [styles.active]: activeTab === 'combined'
          })}
        >
          <Button.Icon>
            <PackageIcon />
          </Button.Icon>
          <Button.Label>Combined View</Button.Label>
        </Button>

        <Button
          aria-label="Combined with Outline"
          variant="tertiary"
          size="small"
          onClick={() => handleTabChange('outline')}
          className={classnames({
            [styles.active]: activeTab === 'outline'
          })}
        >
          <Button.Icon>
            <DiagramIcon />
          </Button.Icon>
          <Button.Label>Combined with Outline</Button.Label>
        </Button>

        <Button
          aria-label="Side by Side"
          variant="tertiary"
          size="small"
          onClick={() => handleTabChange('sideBySide')}
          className={classnames({
            [styles.active]: activeTab === 'sideBySide'
          })}
        >
          <Button.Icon>
            <TargetIcon />
          </Button.Icon>
          <Button.Label>Side by Side</Button.Label>
        </Button>
      </div>

      <div className={styles['tab-content']}>
        {comparisonReport && renderTabContent()}
      </div>
      {isLoading && (
        <div className={styles['loading-overlay']}>
          <LoadingSpinner
            containerStyle={{
              position: 'relative',
              width: 'auto',
              height: 'auto',
              backdropFilter: 'unset'
            }}
          />
          {progress !== null && (
            <div className={styles['progress-container']}>
              <div className={styles['progress-track']}>
                <div
                  className={styles['progress-bar']}
                  style={{ width: `${displayProgress}%` }}
                />
              </div>
              <div className={styles['progress-info']}>
                <span className={styles['progress-label']}>
                  {getProgressLabel(displayProgress)}
                </span>
                <span className={styles['progress-percent']}>
                  {displayProgress}%
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
